import {
  BorrowerSortField,
  ExcludedBorrower,
  ExcludedBorrowersResponse,
  SortOrder as QuerySortOrder,
  useQueryGetExcludedBorrowers,
} from '@oaknorth/api-polaris';
import { CardHeader, CardRoot, Flex, HStack, Table, Text } from '@oaknorth/component-core-v2';
import {
  createColumnHelper,
  SortOrder,
  TableBody,
  TableError,
  TableFilters,
  TableHeader,
  TableLoading,
  TablePagination,
  useServerTable,
} from '@oaknorth/component-table';
import { FormatValue, Money, T, useTranslation } from '@oaknorth/util-intl';
import { InternalTextLink } from '@oaknorth/util-routing';

const columnHelper = createColumnHelper<ExcludedBorrower>();

export function SectorWithCode({ name, code }: { name?: string | null; code?: string | null }) {
  if (!code) {
    return (
      <HStack align="baseline">
        <Text
          as="span"
          color="fg.muted"
          fontSize="sm"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
          overflow="hidden"
        >{T`diagnostics.excludedBorrowers.missingNAICScode`}</Text>
      </HStack>
    );
  }

  return (
    <HStack align="baseline">
      <Text as="span" color="fg.muted" fontSize="sm" fontWeight="semibold">
        {code}
      </Text>
      {/* Truncate the industry name rather than the whole lot */}
      <Text color="fg.muted" fontSize="sm" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
        {name ?? T`diagnostics.excludedBorrowers.NAICSNotSupported`}
      </Text>
    </HStack>
  );
}

const columns = [
  columnHelper.accessor('legal_entity_id', {
    cell: ({ row }) => {
      const { name, naics_info: naicsInfo, legal_entity_id: legalEntityId } = row.original;
      return (
        <>
          <InternalTextLink to={`./${legalEntityId}`}>{name}</InternalTextLink>
          <SectorWithCode name={naicsInfo.name} code={naicsInfo.code} />
        </>
      );
    },
    header: () => T`diagnostics.excludedBorrowers.table.column.borrower`,
  }),
  columnHelper.accessor('exposure', {
    cell: (info) => <FormatValue value={new Money(info.getValue(), 'USD')} />,
    header: () => T`diagnostics.excludedBorrowers.table.column.exposure`,
  }),
  columnHelper.accessor('internal_risk_rating', {
    cell: (info) => info.getValue(),
    header: () => T`diagnostics.excludedBorrowers.table.column.internalRiskRating`,
  }),
];

function getSortParameters(sortOrder: SortOrder) {
  const hasSortOrder = Object.keys(sortOrder).length > 0;
  if (!hasSortOrder) {
    return {};
  }
  const [key, { direction }] = Object.entries(sortOrder)[0];
  const columnToDbFieldMap: Record<string, BorrowerSortField> = {
    legal_entity_id: BorrowerSortField.BorrowerName,
    exposure: BorrowerSortField.BorrowerExposure,
    internal_risk_rating: BorrowerSortField.BorrowerInternalRiskRating,
  };
  return {
    sort_field: columnToDbFieldMap[key],
    sort_order: QuerySortOrder[direction],
  };
}

const rowMapper = (response: ExcludedBorrowersResponse | undefined) => response?.excluded_borrowers;

const INITIAL_SORT_BY = [{ id: 'legal_entity_id', desc: false }];

export function ExcludedBorrowersTable() {
  const { t } = useTranslation();

  const { table, query } = useServerTable({
    useLoadData: ({ pageIndex, pageSize }, sortOrder, nameFilter) =>
      useQueryGetExcludedBorrowers({
        query: {
          ...getSortParameters(sortOrder),
          page: pageIndex + 1,
          page_size: pageSize,
          borrower_name: nameFilter || undefined,
        },
      }),
    columns,
    initialSortBy: INITIAL_SORT_BY,
    enableMultiSort: false,
    rowMapper,
    getRowCount: (response) => response.page_info.total_count,
  });

  return (
    <CardRoot>
      <CardHeader>
        <Flex alignItems="flex-start">
          <TableFilters
            table={table}
            data-testid="excluded-borrowers-filters"
            currentFilters={{}}
            onChangeFilters={() => undefined}
            initialFilters={{}}
            searchPlaceholder={t`diagnostics.excludedBorrowers.table.filter.borrowerName.placeholder`}
            filters={[]}
          />
        </Flex>
      </CardHeader>
      {query.isPending ? (
        <TableLoading table={table} numberOfRows={25} />
      ) : (
        <Table
          css={{ borderCollapse: 'collapse' }}
          data-testid="excluded-borrowers-table"
          borderTop="1px solid"
          borderColor="gray.200"
        >
          <TableHeader table={table} />
          {query.isError && <TableError error={query.error} table={table} />}

          {query.isSuccess && <TableBody table={table} />}
        </Table>
      )}
      {query.isSuccess && <TablePagination table={table} />}
    </CardRoot>
  );
}
